import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A parabolic satellite dish on a pedestal mount: a white reflector with a
// feed arm and sub-reflector, on an altazimuth yoke that slowly sweeps. The
// dish and its mount above the pedestal are named "dish" and rotate in azimuth.
const DISH = "#d6d9dc";
const DISH_DK = "#b3b7ba";
const FEED = "#8a9097";
const MOUNT = "#6c727a";
const PEDESTAL = "#5f656b";

const YAW_Y = 0.46;

function dishAssembly(): Part[] {
  // Built facing +Z and tilted up, all named "dish".
  const tilt = -0.5;
  return [
    // Concave reflector: a shallow bowl, wide at the front face and narrowing to
    // the back, so it reads as a parabolic dish rather than a flat plate.
    { geometry: new THREE.CylinderGeometry(0.44, 0.26, 0.16, 20), color: DISH, position: [0, 0.6, 0.0], rotation: [tilt, 0, 0], name: "dish" },
    { geometry: new THREE.CylinderGeometry(0.38, 0.38, 0.03, 20), color: DISH_DK, position: [0, 0.63, 0.06], rotation: [tilt, 0, 0], name: "dish" },
    // Tripod feed legs converging to a sub-reflector held in front of the dish.
    { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.34, 6), color: FEED, position: [-0.16, 0.74, 0.2], rotation: [tilt + 0.4, 0, 0.4], name: "dish" },
    { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.34, 6), color: FEED, position: [0.16, 0.74, 0.2], rotation: [tilt + 0.4, 0, -0.4], name: "dish" },
    { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.34, 6), color: FEED, position: [0, 0.86, 0.22], rotation: [tilt + 0.4, 0, 0], name: "dish" },
    { geometry: new THREE.SphereGeometry(0.055, 8, 6), color: FEED, position: [0, 0.82, 0.42], name: "dish" },
    // Yoke connecting the dish to the pedestal.
    { geometry: new THREE.BoxGeometry(0.12, 0.16, 0.08), color: MOUNT, position: [0, 0.5, -0.1], name: "dish" },
  ];
}

export const satelliteDish = defineModel({
  id: "satellite-dish",
  batchId: "electric",
  name: "Satellite dish",
  tags: ["prop", "electric", "power", "telecom", "antenna", "dish", "animated"],
  summary: "A parabolic dish on a pedestal that slowly sweeps in azimuth.",
  footprint: 0.45,
  parts(): Part[] {
    return [
      { geometry: new THREE.CylinderGeometry(0.18, 0.22, 0.08, 12), color: PEDESTAL, position: [0, 0.04, 0] },
      { geometry: new THREE.CylinderGeometry(0.07, 0.09, 0.4, 12), color: PEDESTAL, position: [0, 0.26, 0] },
      ...dishAssembly(),
    ];
  },
  animations: [
    {
      id: "sweep",
      label: "Sweeping",
      duration: 9,
      loop: true,
      tracks: [
        { target: "dish", pivot: [0, YAW_Y, 0], rotation: [
          { t: 0, value: [0, -0.7, 0], ease: "sine" }, { t: 4.5, value: [0, 0.7, 0], ease: "sine" }, { t: 9, value: [0, -0.7, 0], ease: "sine" },
        ] },
      ],
    },
  ],
});
